import {
  plannerContactNoteStageSchema,
  isContactNoteStageWrite,
  contactNoteStageSourceValid,
  contactNoteStageEnvelope,
  contactNoteStageClarificationAllowed,
  CONTACT_NOTE_STAGE_GUIDANCE,
} from './planner-first-contact-note-stage.js';
import { plannerClarificationSourceAllowed } from './planner-first-reads.js';
import {
  plannerWriteMissingFieldsAllowed,
  plannerWriteMissingInformation,
} from './planner-first-write-required.js';
import { AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT } from '@govard/ai-chat-kit';
import { z } from 'zod';
import type { IntentEnvelopeV1, IntentOperation } from './intent-envelope.js';
import {
  compoundCandidateChoiceSchema,
  compoundSelectionBindingSchema,
} from './compound-selection.contract.js';

const literal = z.string().trim().min(1).max(2000);
const fact = z.object({ rawValue: literal, sourceQuote: literal }).strict();
const boundedFact = (max: number) => fact.extend({ rawValue: z.string().trim().min(1).max(max) });
const taskName = boundedFact(200);
const common = { sourceQuote: literal };
/** Literal task requests only. Identifiers, versions and normalized dates are server-owned. */
export const plannerFirstWriteSchema = z.discriminatedUnion('capability', [
  ...plannerContactNoteStageSchema.options,
  z
    .object({
      ...common,
      capability: z.literal('create_task'),
      title: taskName.optional(),
      description: boundedFact(1000).optional(),
      dueAt: boundedFact(200).optional(),
      contact: taskName.optional(),
      company: taskName.optional(),
    })
    .strict(),
  z
    .object({
      ...common,
      capability: z.literal('complete_task'),
      target: taskName.optional(),
      status: fact.optional(),
    })
    .strict(),
  z
    .object({
      ...common,
      capability: z.literal('reschedule_task'),
      target: taskName.optional(),
      dueAt: boundedFact(200).optional(),
    })
    .strict(),
]);
export type PlannerFirstWriteV1 = z.infer<typeof plannerFirstWriteSchema>;
export type PlannerFirstWrite = PlannerFirstWriteV1;

export const plannerWriteSelectionSchema = z
  .object({
    type: z.literal('planner-first-write.selection.v1'),
    write: plannerFirstWriteSchema,
    candidates: z
      .array(
        z
          .object({
            entityType: z.enum(['task', 'contact', 'deal', 'company']),
            entityId: z.string().min(1).max(200),
            label: z.string().min(1).max(300),
            version: z.string().datetime({ offset: true }),
            matchedFields: z.array(z.string()).max(20),
          })
          .strict(),
      )
      .min(2)
      .max(AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT),
    consumed: z.boolean(),
    verifierReceipt: z
      .object({
        complete: z.literal(true),
        factsHash: compoundSelectionBindingSchema.shape.contentHash,
      })
      .strict(),
    consumedByMessageId: z.string().min(1).max(200).optional(),
  })
  .strict();
export type PlannerWriteSelection = z.infer<typeof plannerWriteSelectionSchema>;
export const plannerWriteSelectionRequestSchema = z
  .object({
    sourceMessageId: z.string().min(1).max(200),
    revisionId: z.string().min(1).max(200),
    expectedDraftVersion: z.number().int().nonnegative(),
    expectedStateVersion: z.number().int().nonnegative(),
    choice: compoundCandidateChoiceSchema,
  })
  .strict();
export type PlannerWriteSelectionRequest = z.infer<typeof plannerWriteSelectionRequestSchema>;

const completionAliases = new Set([
  'done',
  'completed',
  'complete',
  'выполненной',
  'выполненную',
  'выполненная',
  'выполнена',
  'завершённую',
  'завершенную',
  'завершена',
]);
const normalize = (s: string) => s.toLocaleLowerCase().replaceAll('ё', 'е');
function whole(source: string, token: string): boolean {
  const escaped = normalize(token).replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
  return new RegExp('(^|[^\\p{L}\\p{N}])' + escaped + '(?=$|[^\\p{L}\\p{N}])', 'u').test(
    normalize(source),
  );
}
export function plannerWriteSourceValid(write: PlannerFirstWrite, source: string): boolean {
  if (isContactNoteStageWrite(write)) return contactNoteStageSourceValid(write, source);
  if (!source.includes(write.sourceQuote)) return false;
  for (const [key, value] of Object.entries(write)) {
    if (value === undefined || typeof value === 'string') continue;
    if (
      !write.sourceQuote.includes(value.sourceQuote) ||
      !value.sourceQuote.includes(value.rawValue)
    )
      return false;
    if (
      key === 'status' &&
      (!completionAliases.has(value.rawValue.toLocaleLowerCase()) ||
        !whole(write.sourceQuote, value.rawValue) ||
        !whole(value.sourceQuote, value.rawValue))
    )
      return false;
    if (
      key === 'dueAt' &&
      (!whole(write.sourceQuote, value.rawValue) || !whole(value.sourceQuote, value.rawValue))
    )
      return false;
  }
  return true;
}

/** Missing fields remain explicit; the model cannot supply a default task or deadline. */
export function plannerWriteEnvelope(write: PlannerFirstWrite): IntentEnvelopeV1 {
  if (isContactNoteStageWrite(write)) return contactNoteStageEnvelope(write);
  const missingInformation = plannerWriteMissingInformation(write, true);
  const fields: IntentOperation['fields'] = [];
  const relations: IntentOperation['relations'] = [];
  const target =
    'target' in write && write.target
      ? {
          entity: 'task' as const,
          displayName: write.target.rawValue,
          sourceQuote: write.target.sourceQuote,
        }
      : null;
  if (write.capability === 'create_task') {
    for (const key of ['title', 'description', 'dueAt'] as const) {
      const value = write[key];
      if (value) fields.push({ field: key, ...value });
    }
    for (const entity of ['contact', 'company'] as const) {
      const value = write[entity];
      if (value)
        relations.push({
          field: `${entity}Id`,
          target: { entity, displayName: value.rawValue, sourceQuote: value.sourceQuote },
          sourceQuote: value.sourceQuote,
        });
    }
  } else if (write.capability === 'complete_task') {
    if (write.status) fields.push({ field: 'status', ...write.status });
  } else if (write.dueAt) fields.push({ field: 'dueAt', ...write.dueAt });
  return {
    version: 1,
    mode: 'crm_write',
    confidence: 'high',
    missingInformation,
    unsupportedRequirements: [],
    operations: [
      {
        clientOperationId: 'task',
        entity: 'task',
        operation: write.capability === 'create_task' ? 'create' : 'update',
        target,
        fields,
        relations,
        dependsOn: [],
        sourceQuote: write.sourceQuote,
      },
    ],
  };
}

export const PLANNER_FIRST_WRITE_GUIDANCE = [
  'Task create/complete/reschedule MUST use {kind:"write",write:PlannerFirstWriteV1}; never use kind:simple or combine write with envelope/read. Keep this outer wrapper during repair.',
  'PlannerFirstWriteV1 is a strict union: {capability:"create_task",sourceQuote,title?,description?,dueAt?,contact?,company?} | {capability:"complete_task",sourceQuote,target?,status?} | {capability:"reschedule_task",sourceQuote,target?,dueAt?}.',
  'Compact write example: {"kind":"write","write":{"capability":"complete_task","sourceQuote":"Mark task Audit inventory as done","target":{"rawValue":"Audit inventory","sourceQuote":"Audit inventory"},"status":{"rawValue":"done","sourceQuote":"done"}}}. target is a fact, never {entity,source,displayName}.',
  'Each supplied value is {rawValue,sourceQuote}, copied literally from the current message. sourceQuote on PlannerFirstWriteV1 covers the complete single operation. No IDs, versions, normalized dates or invented defaults.',
  'complete_task status must literally say done/completed or the corresponding completion inflection. reschedule_task changes dueAt only; date-only keeps the stored clock and time-only keeps the stored day, resolved by the server. Missing target/title/deadline stays omitted for a named field clarification.',
  CONTACT_NOTE_STAGE_GUIDANCE,
  'Only the listed typed operations are supported here. Delete, multiple actions, extra fields, sends, conditions or unsupported relations must be unsupported in full, never discarded.',
].join('\n');

/** The model may identify a missing typed write fact; real candidate ambiguity remains server-owned. */
export function plannerWriteClarificationAllowed(
  source: string,
  write: PlannerFirstWrite,
  missing: readonly { field: string; reason: string }[],
): boolean {
  if (!plannerWriteClarificationSourceAllowed(source, write) || missing.length === 0) return false;
  if (isContactNoteStageWrite(write)) return contactNoteStageClarificationAllowed(write, missing);
  return plannerWriteMissingFieldsAllowed(write, missing);
}

export function plannerWriteClarificationSourceAllowed(
  source: string,
  write: PlannerFirstWrite,
): boolean {
  if (!plannerWriteSourceValid(write, source)) return false;
  if (isContactNoteStageWrite(write)) return true;
  if (
    !['task', 'задача', 'задачу', 'задачи', 'задаче', 'задачей'].some((word) => whole(source, word))
  )
    return false;
  if (!plannerClarificationSourceAllowed(source)) return false;
  return true;
}
